
import { Base } from "./Base";
import { IBase, ILocation, ISize, IPerson, IFile, IMessage, 
         EMessageType, ENotificationType, 
         IContact, ITeam, ETeamType, EContactStatus, 
         IChannel, EChannelType, Image, 
         IPostProperties, IUserProperties, IFileProperties
} from "./shared/types";

import i18n, 
       { transformDateRecent, transformDateFormatted } 
from "@/common/filters";

import { User } from "./User";
import { Contact } from "./Contact";
import { Channel } from "./Channel";

import { isString, createDate } from "@/common/utils";

const TIME_OUT = 0;
const TIME_INTERVAL = 5;
const MAX_TRIES = 500;
const GOOGLE_STATIC_MAP_URL = 'https://maps.googleapis.com/maps/api/staticmap?zoom=15&maptype=roadmap&format=png&scale=1';

export class Message extends Base implements IMessage {
  HalloId: string;
  Type: EMessageType;
  Category: ENotificationType;
  Sender: Contact;
  DateSent: Date;
  Text: string;
  SourceUri: string;
  Location: ILocation;
  Read: number;
  DateRead: Date;

  File: IFile;

  Channel: Channel;

  ListIndex?:number; //for display in ListView purpose
  
  private _view:any;
  private _viewLoaded:boolean = false;

  protected _propertyNames = [
    'HalloId', 'Type', 'Category', 'Sender', 'DateSent', 'Text', 'SourceUri', 'Location', 'File',
    'Read', 'DateRead', 'Channel'
  ];

  constructor(message?:IMessage) {
    super();
    if (message) {
      for (let p in message) {
        if (this._propertyNames.indexOf(p) != -1) {
          this[p] = message[p];
        }
      }
    }
  }

  set PropertiesFromStorage(data) {
    if (!(data && data.id)) {
      return;
    }
    if (data.id!=this.HalloId) {
      this.HalloId = data.id;
    }
    if (data.create_ts) {
      this.DateSent = createDate(data.create_ts);
    }
    if (data.text && data.text!=this.Text) {
      this.Text = data.text;
    }
    if (data.sender) {
      this.Sender = this.Sender || new Contact();
      this.Sender.PropertiesFromStorage = data.sender;
    }
  }

  mapChatPost(post:IPostProperties) {
    this.HalloId = post.id;
    this.DateSent = new Date(post.create_at);
    this.Text = post.message;
    if (this.Text) {
      this.Type = EMessageType.Text;
    }
  }

  get Index() : number {
    return this.Channel.Messages.indexOf(this);
  }

  get VideoType() : string {
    if (this.Type==EMessageType.Video && this.SourceUri) {
      let ext = this.SourceUri.split('.');
      return 'video/' + ext[ext.length - 1];
    }
    return '';
  }

  get ReadText() : string {
    if (this.Read==0) {
      return '';
    } else if (this.Channel.Type==EChannelType.Direct || this.Channel.isSingleContact) {
      return i18n.L('read');
    } else {
      return i18n.L('read') + ' ' + this.Read;
    }
  }

  get DateSentRecent() : string {
    return transformDateRecent(this.DateSent, 'yyyy MMM dd');
  }

  get UntilDate() : string {
    let d = new Date().getTime() + 10*24*60*60*1000;
    return transformDateFormatted(new Date(d),'yyyy-MM-dd h:m 12h')
  }

  get MaxBubbleWidth() : number {
    return this.DisplayWidth - 180;
  }

  get MaxBubbleHeight() : number {
    return Math.floor(this.MaxBubbleWidth * 9 / 16);
  }

  get BubbleSize() : ISize {
    return {
      width: this.MaxBubbleWidth,
      height: Math.floor(this.MaxBubbleWidth * 9 / 16)
    }
  }

  get MapSize() : ISize {
    return {
      width: this.MaxBubbleWidth,
      height: Math.floor(this.MaxBubbleWidth * 9 / 16)
    }
  }

  get MapWidth() : number {
    return this.MaxBubbleWidth;
  }

  get MapHeight() : number {
    return Math.floor(this.MaxBubbleWidth * 9 / 16);
  }
  
  get MapURL() : string {
    this.setLocationName();
    let params = '&size=' + this.MapWidth + 'x' + this.MapHeight;
    if (this.Type==EMessageType.Location) {
      params += '&center=' + this.Location.Latitude + ',' + this.Location.Longitude;
      //params += '&markers=color:0x0000FF%7C' + this.Location.Latitude + ',' + this.Location.Longitude;
    } else {
      params += '&center=13.4567,100.5684'; //default
      //params += '&markers=color:0xFF0000%7C13.4567,100.5684'; //default
    }
    return GOOGLE_STATIC_MAP_URL + params;
  }

  private setClipPath(view, width:number, height:number, align:string) {
    this._view = view;
    let brs = 6;//4;// * srn.mainScreen.scale;
    let dxy = 4.242641;//2.828427;// * srn.mainScreen.scale; //cos(45) * brs
    let cp:string;
    brs = 1;
    view.width = width;
    view.height = height;
    height--;
    if (align=='from') {
      width--;
      /*cp = 'polygon(' + (8+brs) + ' 0,' + 
          (width-brs) + ' 0,' + (width-brs+dxy) + ' ' + (brs-dxy) + ',' + width + ' ' + brs + ',' + 
          width + ' ' + (height-brs) + ',' +  (width-brs+dxy) + ' ' + (height-brs+dxy) + ',' + (width-brs) + ' ' + height + ',' + 
          (8+brs) + ' ' + height + ',' + (8+brs-dxy) + ' ' + (height-brs+dxy) + ',' + '8 ' + (height-brs) + ',' + 
          '8 21,0 15,8 10,8 ' + brs + ',' +  (8+brs-dxy) + ' ' + (brs-dxy) + ')';
      */
      cp = 'polygon(' + (8+brs) + ' 0,' + (width-brs) + ' 0,' + 
            width + ' ' + brs + ',' + width + ' ' + (height-brs) + ',' +  
            (width-brs) + ' ' + height + ',' + (8+brs) + ' ' + height + ',' + 
            '8 ' + (height-brs) + ',' + '8 21,0 15,8 10,8 ' + brs + ')';
    } else {
      let r = width - 8;
      /*cp = 'polygon(' + brs + ' 0,' + 
          (r-brs) + ' 0,' + (r-brs+dxy) + ' ' + (brs-dxy) + ',' + r + ' ' + brs + ',' + 
          r + ' 10,' + width + ' 15,' + r + ' 21,' + 
          r + ' ' + (height-brs) + ',' + (r-brs+dxy) + ' ' + (height-brs+dxy) + ', ' + (r-brs) + ' ' + height + ',' +
          brs + ' ' + height + ',' + (brs-dxy) + ' ' + (height-brs+dxy) + ',' + '0 ' + (height-brs) + ',' + 
          '0 ' + brs + ',' +  (brs-dxy) + ' ' + (brs-dxy) + ')';
      */
      cp = 'polygon(' + (brs+1) + ' 0,' + (r-brs) + ' 0,' + 
            r + ' ' + brs + ',' + r + ' 10,' + width + ' 15,' + r + ' 21,' + 
            r + ' ' + (height-brs) + ',' + (r-brs) + ' ' + height + ',' + 
            brs + ' ' + height + ',' + '1 ' + (height-brs-1) + ',1 ' + brs + ')';
    }
    view.style.clipPath = cp;
    view.visibility = 'visible';
  }

  calcTextSize(text, maxWidth:number) : ISize {
    //to do
    return {
      width: Math.min(100, maxWidth),
      height: 40
    };
  }

  measureTextHeight(text?:string) : number {
    // todo calc height of text
    return 24;
  }

  loadText(args) {
    let view = args.object;
    let paddingW:number = view.paddingLeft + view.paddingRight + 2;
    let maxWidth = this.MaxBubbleWidth - paddingW;
    let text = (this.Text + '');
    let n:number = 1;
    let ti = setInterval(() => {
      let align = view.align + '';
      let str = view.text + '';
      if ((str && align) || (n>MAX_TRIES)) {
        clearInterval(ti);
        let size = this.calcTextSize(view, maxWidth);
        this.setClipPath(view, size.width, size.height, view.align);
      }
      n++;
    }, TIME_INTERVAL);
  }

  AndroidloadText(args) {
    let view = args.object;
    let paddingW:number = view.paddingLeft + view.paddingRight + 2;
    let tw:number=0, th:number = this.measureTextHeight();
    let vw:number=0, vh:number = view.paddingTop + view.paddingBottom + 2;
    let align:string = (view.align + '');
    let strs,str:string = (this.Text + '');
    let size = view.getActualSize();
    if (!str) {
      vw = Math.max(th, size.width);
      vh = Math.max(th, size.height);
      this.setClipPath(view, vw, vh, align);
    } else {
      let ti = setInterval(() => {
        let n:number = 1;
        align = view.align + '';
        if (align || (n>MAX_TRIES)) {
          clearInterval(ti);
          let i:number, m:number;
          let maxWidth:number = this.MaxBubbleWidth - paddingW;
          //find maxWidth
          strs = str.split('\n');
          for (i=0; i<strs.length; i++) {
            tw = this.calcTextSize(strs[i],100).width;
            if (tw > maxWidth) {
              vw = maxWidth;
              break;
            } else {
              vw = (tw>vw) ? tw : vw;
            }
          }
          size = this.calcTextSize(view, vw);
          vw = size.width;
          vh = size.height;
          /*
          if (str) {
            strs = str.split('\n');
            for (i=0; i<strs.length; i++) {
              tw = screen.measureTextWidth(view,strs[i]);
              if (tw > maxWidth) {
                m = Math.ceil(tw / maxWidth);
                vw = maxWidth;
                vh += th * m;
                //break;
              } else {
                vw = (tw>vw) ? tw : vw;
                vh += th;
              }
            }
          } else {
            vw = th;
            vh = th;
          }*/
          this.setClipPath(view, vw, vh, align);
        }
        n++;
      }, TIME_INTERVAL);
    }
  }

  private setImageBubble(view:Image, width:number, height:number, align:string) {
    let maxWidth:number = this.MaxBubbleWidth;
    let wh:number;
    wh = width / height;
    if (width > height) {
      width = Math.min(width,maxWidth);
      height = width / wh;
    } else {
      height = Math.min(height,maxWidth);
      width = height * wh;
    }
    this.setClipPath(view,width, height, align);
  }

  loadImage(args) {
    let view = args.object;
    setTimeout(() => {
      let align:string = view.align + '';
      let src:string;
      src = this.SourceUri;
      if (src.match(/^http/i)) {
      } else {
      }
    }, TIME_OUT); //Math.min(5,TIME_OUT));
  }

  private setLocationName() {
    this.Location.Name = this.Location.Latitude + '';
    this.Location.Vicinity = this.Location.Longitude + '';
  }

  loadLocation(args) {
    let view = args.object;
    let size = this.MapSize
    setTimeout(() => {
      let align:string = view.align + '';
      this.setClipPath(view, size.width, size.height, align);
      this.setLocationName();
    }, TIME_OUT);
  }

  showLocation(args) {
    //show map page at this location
    console.log('showLocation ' + args.object.index);
    /*setTimeout(() => {
      frame.topmost().navigate({
        moduleName: 'pages/chat/video',
        context: {
          channel: this.Channel,
          user: frame.topmost().currentPage.navigationContext.user,
          videoPath: this.SourceUri
        },
        transition: { name: 'slideBottom' }
      });
    }, 0);*/
  }

  loadVideo(args) {
    let view = args.object;
    let maxWidth = this.MaxBubbleWidth;
    let size = this.BubbleSize;
    setTimeout(() => {
      let align:string = view.align;
      this.setClipPath(view,size.width, size.height, align);
    }, TIME_OUT);
  }

  playVideo(args) {
    setTimeout(() => {
    }, 0);
  }

  loadFile(args) {
    let view = args.object;
    let size = this.BubbleSize;
    size.height = 70;
    setTimeout(() => {
      let align:string = view.align + '';
      this.setClipPath(view, size.width, size.height, align);
    }, TIME_OUT);
  }

  loadSticker(args) {
    this._view = args.object;
    setTimeout(() => {
    }, TIME_OUT);
  }

  manageMessage(args) {
    console.log('Manage Message: ' + (this.Text || this.SourceUri)); 
  }

  showSenderAvatar(args) {
    console.log('src: ' + this.Sender.Avatar); 
    /*setTimeout(() => {
      frame.topmost().navigate({
        moduleName: 'pages/chat/video',
        context: {
          channel: this.Channel,
          user: frame.topmost().currentPage.navigationContext.user,
          videoPath: this.SourceUri
        },
        transition: { name: 'slideBottom' }
      });
    }, 0);*/
  }
}
